/* ===== Input Component ===== */

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (props, ref) => {
    const {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = true,
      className = '',
      id,
      disabled,
      value,
      ...restProps
    } = props

    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined)
    const inputValue = 'value' in props ? (value ?? '') : undefined

    return (
      <div className={`${fullWidth ? 'w-full' : ''} flex flex-col gap-1.5`}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          {leftIcon && (
            <div className="pointer-events-none absolute left-3.5 flex items-center text-neutral-400">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            value={inputValue}
            className={`
              h-11 w-full rounded-[13px] border text-sm transition-colors duration-150
              bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400
              focus:outline-none focus:ring-2 focus:ring-primary-500/20
              disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:text-neutral-500 disabled:cursor-not-allowed
              ${leftIcon ? 'pl-10' : 'pl-3.5'}
              ${rightIcon ? 'pr-10' : 'pr-3.5'}
              ${
                error
                  ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20'
                  : 'border-neutral-200 dark:border-neutral-800 focus:border-primary-500'
              }
              ${className}
            `}
            {...restProps}
          />
          {rightIcon && (
            <div className="absolute right-3.5 flex items-center text-neutral-400">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="text-xs font-semibold text-danger-600 dark:text-danger-200">{error}</p>}
        {!error && helperText && <p className="text-xs text-neutral-500 dark:text-neutral-400">{helperText}</p>}
      </div>
    )
  },
)

Input.displayName = 'Input'
